import { api, type PasskeyRecord } from "../api/client";
import {
  assertionToJSON,
  creationOptionsFromJSON,
  registrationToJSON,
  requestOptionsFromJSON,
  type CreationOptionsJSON,
  type CredentialLike,
  type RequestOptionsJSON,
} from "./webauthn";

/**
 * Passkey ceremonies against helm-api (SPEC §20). Sign-in is usernameless: the OS offers the
 * HELM passkeys it holds. On success the API has set the session cookie.
 */
export async function signInWithPasskey(): Promise<void> {
  const ch = await api.passkeyLoginStart();
  const cred = await navigator.credentials.get({
    publicKey: requestOptionsFromJSON(ch.options as RequestOptionsJSON),
  });
  if (!cred) throw new Error("No passkey was chosen.");
  await api.passkeyLoginFinish(ch.ceremony_id, assertionToJSON(cred as unknown as CredentialLike));
}

/**
 * Step-up: prove one of this account's passkeys again in the signed-in session (ADR-0035), e.g.
 * before setting the sign-in PIN. The server remembers it for a few minutes.
 */
export async function reverifyWithPasskey(): Promise<void> {
  const ch = await api.reverifyStart();
  const cred = await navigator.credentials.get({
    publicKey: requestOptionsFromJSON(ch.options as RequestOptionsJSON),
  });
  if (!cred) throw new Error("No passkey was chosen.");
  await api.reverifyFinish(ch.ceremony_id, assertionToJSON(cred as unknown as CredentialLike));
}

/** Create a passkey on this device's authenticator and register it (approved device only). */
export async function addPasskey(name: string): Promise<PasskeyRecord> {
  const ch = await api.passkeyRegisterStart();
  const cred = await navigator.credentials.create({
    publicKey: creationOptionsFromJSON(ch.options as CreationOptionsJSON),
  });
  if (!cred) throw new Error("No passkey was created.");
  return api.passkeyRegisterFinish(
    ch.ceremony_id,
    name,
    registrationToJSON(cred as unknown as CredentialLike),
  );
}
